import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Pencil, Plus, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { BrowserCompatWrapper } from '@/components/ui/BrowserCompatWrapper';
import { AuditInfo } from './AuditInfo';
import { DateTimeText } from './DateTimeText';
import { NotificationStatus } from './NotificationStatus';
import { OfficialAccommodationLink } from './OfficialAccommodationLink';
import { PreRegisteredAccommodationFields } from './PreRegisteredAccommodationFields';
import { PreRegistrationStatusBadge } from './PreRegistrationStatusBadge';
import { RepatMatchesBlock } from './RepatMatchesBlock';
import {
  useCreatePreRegisteredAccommodation,
  useUpdateAndLinkPreRegisteredAccommodation,
  useUpdatePreRegisteredAccommodation,
} from '../hooks/usePreRegisteredAccommodationMutations';
import {
  preRegisteredAccommodationSchema,
  type PreRegisteredAccommodationFormValues,
} from '../schemas/pre-registered-accommodation.schema';
import { toAccommodationFormValues, toAccommodationPayload } from '../utils/form-mapping.utils';
import { getApiErrorMessage, getApiErrorStatus } from '../utils/api-error.utils';
import { isSameMinute } from '../utils/format.utils';
import { isRowDataEditable, isRowDeleted } from '../utils/status-transitions.utils';
import type { MunicipalListAccommodation } from '../types/pre-registration.types';
import type { LinkedOfficialAccommodation } from '../types/official-accommodation.types';

export type AccommodationSheetMode = 'create' | 'edit' | 'view';

interface PreRegisteredAccommodationSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: AccommodationSheetMode;
  listId: string;
  municipalityId: number;
  // Department of the list's municipality: REPAT matches there are listed first
  departmentId?: number;
  row?: MunicipalListAccommodation;
  // Write roles may switch a view into edit when the status allows it
  canEdit: boolean;
}

const FORM_ID = 'pre-registered-accommodation-form';

// Backend message when the picked official accommodation no longer exists
const LINK_NOT_FOUND = /alojamiento oficial no encontrado/i;

// Create: the title; view/edit: the eyebrow above the establishment name
const TITLES: Record<AccommodationSheetMode, string> = {
  create: 'Nuevo alojamiento preinscripto',
  edit: 'Editar alojamiento preinscripto',
  view: 'Alojamiento preinscripto',
};

// The status badge is in the sheet header
const RowStatusSummary: React.FC<{ row: MunicipalListAccommodation }> = ({ row }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border bg-gray-50 p-3 text-sm">
    <span className="text-gray-600">
      Estado actualizado el <DateTimeText value={row.statusDate} />
    </span>
    {row.accommodation && (
      <span className="flex flex-wrap items-center gap-x-2 text-gray-700">
        Alojamiento oficial: legajo {row.accommodation.fileNumber} —{' '}
        {row.accommodation.establishmentName}
        <OfficialAccommodationLink accommodationId={row.accommodation.id} />
      </span>
    )}
    {/* A deleted row that was never notified has nothing to show here */}
    {(row.notifiedAt || !isRowDeleted(row)) && <NotificationStatus row={row} />}
  </div>
);

// Side panel: the row form has many fields, and KER-568 adds REPAT matches under the name
export const PreRegisteredAccommodationSheet: React.FC<PreRegisteredAccommodationSheetProps> = ({
  open,
  onOpenChange,
  mode: initialMode,
  listId,
  municipalityId,
  departmentId,
  row,
  canEdit,
}) => {
  const [mode, setMode] = useState<AccommodationSheetMode>(initialMode);
  // Official accommodation picked in the REPAT matches; sent only when saving
  const [linked, setLinked] = useState<LinkedOfficialAccommodation | null>(null);
  // Remounts the matches block so it follows the trade name again after "Guardar y agregar otro"
  const [matchesKey, setMatchesKey] = useState(0);
  const createMutation = useCreatePreRegisteredAccommodation(listId);
  const updateMutation = useUpdatePreRegisteredAccommodation();
  const updateAndLinkMutation = useUpdateAndLinkPreRegisteredAccommodation();
  const isSaving =
    createMutation.isPending || updateMutation.isPending || updateAndLinkMutation.isPending;

  const form = useForm<PreRegisteredAccommodationFormValues>({
    resolver: zodResolver(preRegisteredAccommodationSchema),
    defaultValues: toAccommodationFormValues(row),
  });

  // Each opening starts from the requested mode and the row's saved values
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      form.reset(toAccommodationFormValues(row));
      setLinked(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode, row?.id]);

  const readOnly = mode === 'view';
  const canSwitchToEdit = readOnly && canEdit && !!row && isRowDataEditable(row.status.code);
  // Linking from the form is a "Pendiente de control" → "Ya registrado" change
  const showMatches =
    canEdit && (mode === 'create' || (mode === 'edit' && row?.status.code === 'pending_review'));

  const handleError = (error: unknown) => {
    // Locality errors belong next to the locality select
    const message = getApiErrorMessage(error, '');
    if (getApiErrorStatus(error) !== 400) return;
    if (/localidad/i.test(message)) form.setError('localityId', { message });
    // Nothing was created: the user picks again or saves without a link
    if (LINK_NOT_FOUND.test(message)) setLinked(null);
  };

  const save = (values: PreRegisteredAccommodationFormValues, addAnother: boolean) => {
    const payload = toAccommodationPayload(values);
    if (mode === 'create') {
      // One request: with a link the row is created already in "Ya registrado" (KER-572)
      createMutation.mutate(
        { ...payload, accommodationId: linked?.id ?? null },
        {
          onSuccess: () => {
            if (!addAnother) return onOpenChange(false);
            // Keep the panel open for the next row of the municipality's list
            form.reset(toAccommodationFormValues());
            setLinked(null);
            setMatchesKey((key) => key + 1);
            form.setFocus('establishmentName');
          },
          onError: handleError,
        }
      );
    } else if (row && linked && showMatches) {
      updateAndLinkMutation.mutate(
        { id: row.id, payload, accommodationId: linked.id },
        { onSuccess: () => onOpenChange(false), onError: handleError }
      );
    } else if (row) {
      updateMutation.mutate(
        { id: row.id, payload },
        { onSuccess: () => onOpenChange(false), onError: handleError }
      );
    }
  };

  // Read during render: react-hook-form only tracks isDirty for subscribed fields of formState
  const { isDirty } = form.formState;
  // Typed data is not lost by a stray click outside the panel or Escape; Cancelar still closes
  const preventDirtyClose = (event: Event) => {
    if (!readOnly && (isDirty || linked)) event.preventDefault();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
        onInteractOutside={preventDirtyClose}
        onEscapeKeyDown={preventDirtyClose}
      >
        <SheetHeader className="border-b px-6 py-4 pr-12">
          {/* display: contents keeps SheetHeader's layout; the spacing goes on the wrapper */}
          {row && mode !== 'create' ? (
            <BrowserCompatWrapper className="space-y-1">
              <SheetDescription className="text-xs font-medium uppercase tracking-wide">
                {TITLES[mode]}
              </SheetDescription>
              <div className="flex flex-wrap items-center gap-2">
                <SheetTitle>{row.establishmentName}</SheetTitle>
                <PreRegistrationStatusBadge status={row.status} />
              </div>
            </BrowserCompatWrapper>
          ) : (
            <BrowserCompatWrapper className="space-y-2">
              <SheetTitle>{TITLES[mode]}</SheetTitle>
              <SheetDescription>
                {linked
                  ? `El alojamiento se agrega como "Ya registrado", vinculado al legajo ${linked.fileNumber}.`
                  : 'El alojamiento se agrega en estado "Pendiente de control".'}
              </SheetDescription>
            </BrowserCompatWrapper>
          )}
        </SheetHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-4">
          {row && <RowStatusSummary row={row} />}

          <Form {...form}>
            <form id={FORM_ID} onSubmit={form.handleSubmit((values) => save(values, false))}>
              <PreRegisteredAccommodationFields
                municipalityId={municipalityId}
                readOnly={readOnly}
                afterEstablishmentName={
                  showMatches && (
                    <RepatMatchesBlock
                      key={matchesKey}
                      mode={mode === 'create' ? 'create' : 'edit'}
                      departmentId={departmentId}
                      linked={linked}
                      onLinkedChange={setLinked}
                    />
                  )
                }
              />
            </form>
          </Form>

          {row && (
            <AuditInfo
              createdAt={row.createdAt}
              updatedAt={row.updatedAt}
              // The status summary already shows it when the status change was the last edit
              hideUpdatedAt={isSameMinute(row.statusDate, row.updatedAt)}
            />
          )}
        </div>

        <SheetFooter className="gap-2 border-t px-6 py-4 sm:space-x-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {readOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          {canSwitchToEdit && (
            <Button onClick={() => setMode('edit')}>
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {mode === 'create' && (
            <Button
              variant="outline"
              disabled={isSaving}
              onClick={form.handleSubmit((values) => save(values, true))}
            >
              <Plus className="mr-2 h-4 w-4" />
              Guardar y agregar otro
            </Button>
          )}
          {!readOnly && (
            <Button type="submit" form={FORM_ID} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {mode === 'create' ? 'Agregar' : 'Guardar'}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
